import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { createLogger } from '../utils/logger';
import { useToast } from '../context/ToastContext';
import { useDarkMode } from '../context/DarkModeContext';

const logger = createLogger({ module: 'MFASetup', level: 'info' });

interface MFASetupProps {
  onComplete?: () => void;
  onCancel?: () => void;
}

const MFASetup: React.FC<MFASetupProps> = ({ onComplete, onCancel }) => {
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'setup' | 'verify'>('setup');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [hasCopiedCodes, setHasCopiedCodes] = useState(false);
  const { showToast } = useToast();
  const { darkMode } = useDarkMode();

  useEffect(() => {
    startMFAEnrollment();
  }, []);

  const startMFAEnrollment = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp'
      });

      if (error) {
        throw error;
      }

      if (!data || !data.totp) {
        throw new Error('Failed to enroll in MFA');
      }

      setQrCode(data.totp.qr_code);
      setFactorId(data.id);
      setStep('setup');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to setup MFA';
      logger.error('MFA enrollment error:', err);
      setError(errorMessage);
      showToast('Failed to setup MFA: ' + errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const verifyMFA = async () => {
    setLoading(true);
    setError(null);

    if (!factorId) {
      setError('Setup process not initialized properly. Please try again.');
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: verificationCode
      });

      if (error) {
        throw error;
      }

      if (!data) {
        throw new Error('Verification failed');
      }

      // Get recovery codes
      const { data: recoveryData, error: recoveryError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

      if (recoveryError) {
        logger.warn('Failed to fetch recovery codes:', recoveryError);
      } else if (recoveryData?.amr) {
        // Extract recovery codes if available
        const totpFactor = recoveryData.amr.find((method: { method: string; recovery_codes?: string[] }) => method.method === 'totp');
        if (totpFactor && totpFactor.recovery_codes) {
          setRecoveryCodes(totpFactor.recovery_codes);
        }
      }

      showToast('Two-factor authentication setup successfully!', 'success');
      setStep('verify');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Verification failed';
      logger.error('MFA verification error:', err);
      setError(errorMessage);
      showToast('MFA verification failed: ' + errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const copyRecoveryCodes = () => {
    if (recoveryCodes.length === 0) return;
    
    const text = 'IMPORTANT: Save these recovery codes in a safe place.\n\n' + 
                 recoveryCodes.join('\n') + 
                 '\n\nYou can use these codes to access your account if you lose your authenticator device.';
    
    navigator.clipboard.writeText(text)
      .then(() => {
        setHasCopiedCodes(true);
        showToast('Recovery codes copied to clipboard', 'success');
      })
      .catch((err) => {
        logger.error('Failed to copy recovery codes:', err);
        showToast('Failed to copy recovery codes', 'error');
      });
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
  };

  if (loading && !qrCode) {
    return (
      <div className={`p-6 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'} rounded-lg shadow-md`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4">Setting up two-factor authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'} rounded-lg shadow-md max-w-lg mx-auto`}>
      {step === 'setup' && (
        <>
          <h2 className="text-xl font-bold mb-4">Set Up Two-Factor Authentication</h2>
          
          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
              {error}
            </div>
          )}
          
          <div className="mb-4">
            <p className="mb-2">1. Scan this QR code with your authenticator app:</p>
            <div className="flex justify-center p-2 bg-white rounded">
              {qrCode && (
                <img 
                  src={qrCode} 
                  alt="QR Code for authenticator app" 
                  className="h-48 w-48"
                />
              )}
            </div>
          </div>
          
          <div className="mb-6">
            <p className="mb-2">2. Enter the verification code from your authenticator app:</p>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\s/g, ''))}
              placeholder="123456"
              className={`w-full p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-black'}`}
              maxLength={6}
            />
          </div>
          
          <div className="flex justify-between">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 bg-gray-200 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            
            <button
              onClick={verifyMFA}
              disabled={loading || verificationCode.length !== 6}
              className={`px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 ${
                (loading || verificationCode.length !== 6) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        </>
      )}
      {step === 'verify' && (
        <>
          <h2 className="text-xl font-bold mb-4">Two-Factor Authentication Enabled</h2>
          
          <div className="mb-6">
            <p className="mb-4">Your two-factor authentication has been set up successfully!</p>
            
            {recoveryCodes.length > 0 && (
              <div className="mt-6">
                <h3 className="font-bold mb-2">Recovery Codes</h3>
                <p className="text-sm mb-3 text-yellow-500">
                  <strong>Important:</strong> Save these recovery codes in a safe place. You'll need them if you lose access to your authenticator app.
                </p>
                
                <div className={`p-3 rounded-md mb-4 font-mono text-sm ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  {recoveryCodes.map((code, index) => (
                    <div key={index} className="mb-1">{code}</div>
                  ))}
                </div>
                
                <button
                  onClick={copyRecoveryCodes}
                  className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 mb-4 w-full"
                >
                  {hasCopiedCodes ? 'Copied!' : 'Copy Recovery Codes'}
                </button>
              </div>
            )}
          </div>
          
          <button
            onClick={handleComplete}
            disabled={!hasCopiedCodes && recoveryCodes.length > 0}
            className={`px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700 w-full ${
              (!hasCopiedCodes && recoveryCodes.length > 0) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {!hasCopiedCodes && recoveryCodes.length > 0 
              ? 'Please Copy Recovery Codes First' 
              : 'Complete Setup'}
          </button>
        </>
      )}
    </div>
  );
};

export default MFASetup; 